import React, { useEffect } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useForm, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";

import { factoryUseModalX } from "@/lib/factoryUseModalX";
import fetchSingleton from "@/lib/fetchSingleton";
import { submitFunction, useFormValidation } from "@/lib/formValidationHelpers";
import { useAuth } from "@/lib/useAuth";
import { useData, useDataList } from "@/lib/useData";
import PartFormError from "@/PartFormError";
import PartFormMultiselect from "@/PartFormMultiselect";
import PartFormSaveButton from "@/PartFormSaveButton";
import PartLoader from "@/PartLoader";
import PartModal, { type PartModalRef } from "@/PartModal";
import formStyles from "@/styles/form.module.scss";
import { Program } from "@/types/api-types";
import { userEditorSchemaJoi } from "@/validations";

interface IFormInput {
  email: string;
  program_ids: number[] | string[] | null;
  role_id: number;
  name: string;
}

interface IFormInputRef {
  reset: () => void;
}

export const useModalUserEditor = factoryUseModalX({
  queryName: "modalUserEditor",
});

const UserEditor = React.forwardRef<
  IFormInputRef,
  { setConfirmClose?: (arg0: boolean) => void }
>(({ setConfirmClose }, ref) => {
  const { isCreating, isEditing, editingId, finishInteraction } = useModalUserEditor();

  const formMethods = useForm<IFormInput>({
    reValidateMode: "onSubmit",
  });

  const {
    register,
    control,
    formState: { errors, isDirty },
    reset,
    setError,
    clearErrors,
    watch,
    setValue,
    trigger,
  } = formMethods;

  React.useImperativeHandle(ref, () => ({
    reset: () => reset(),
  }));

  const { handleSubmit, isSubmitting } = useFormValidation<IFormInput>({
    control,
    joiSchema: userEditorSchemaJoi,
    context: { isEditing, editingId },
    setError,
    clearErrors,
  });

  const [myself, isMyselfLoading] = useData<{ id: number; email: string }>(
    process.env.NEXT_PUBLIC_API_URL + "/api/v1/user/me",
  );

  const [
    existingItem,
    isExistingItemLoading,
    {
      meta: { acl: _acl },
    },
  ] = useData<{
    id: number;
    name: string;
    email: string;
    role: { id: number };
    programs: { id: number }[] | null;
  }>(isEditing ? process.env.NEXT_PUBLIC_API_URL + `/api/v1/user/${editingId}` : "");

  const acl = _acl as {
    name: string;
    email: string;
    role_id: string;
    program_ids: string;
  }[];

  const [roles, isRoleLoading] = useDataList<{ id: number; code: string; name: string }>(
    process.env.NEXT_PUBLIC_API_URL + "/api/v1/enum/roles",
  );
  const [programs, isProgramsLoading] = useDataList<Program>(
    process.env.NEXT_PUBLIC_API_URL + "/api/v1/program",
  );

  useEffect(() => {
    if (isEditing && existingItem) {
      const existingItem1 = {
        name: existingItem.name,
        email: existingItem.email,
        role_id: existingItem.role?.id,
        program_ids: existingItem.programs?.map(it => String(it?.id)) || [],
      };
      reset(existingItem1);
    }
    if (isCreating)
      reset({
        program_ids: [],
      });
  }, [isCreating, isEditing, existingItem, isRoleLoading, isProgramsLoading]);

  const { idToken, signOut } = useAuth();

  const onSubmit: submitFunction<IFormInput> = async (data, handleApiResult) => {
    let url = process.env.NEXT_PUBLIC_API_URL + "/api/v1/user";
    if (isEditing) url += "/" + editingId;

    const shouldKillUserSession =
      myself?.id === existingItem?.id && existingItem?.email !== data.email;

    const result = await fetchSingleton(url, {
      method: (isCreating && "POST") || (isEditing && "PUT") || "GET",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      // Here we need noSideEffects to be true to prevent reload data on whole frontend, because session is invalid
      // after changing email
      noSideEffects: shouldKillUserSession,
    });

    handleApiResult(result);

    if (shouldKillUserSession) {
      await signOut();
      return;
    }

    const msg = (() => {
      if (isCreating) return "User added successfully";
      if (isEditing) return "User updated successfully";
    })();
    toast.success(msg, { autoClose: 5000 });

    finishInteraction();
  };

  React.useEffect(() => {
    if (setConfirmClose) setConfirmClose(isDirty);
  }, [isDirty]);

  const isSelectedSuperAdmin = !!(
    watch("role_id") &&
    String(watch("role_id")) === String(roles?.find(it => it?.code === "sa")?.id)
  );

  React.useEffect(() => {
    if (isSelectedSuperAdmin) {
      setValue("program_ids", programs?.map(it => String(it?.id)) || []);
      trigger("program_ids").then();
    }
  }, [isSelectedSuperAdmin, programs]);

  return (
    <FormProvider {...formMethods}>
      <PartLoader
        block
        active={
          isExistingItemLoading || isRoleLoading || isProgramsLoading || isMyselfLoading
        }
      >
        <form
          className={formStyles.form}
          onSubmit={handleSubmit(onSubmit)}
          onReset={() => {
            finishInteraction();
          }}
          autoComplete="off"
        >
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="name">
                Full Name <sup>*</sup>
              </label>
              <input
                id="name"
                className={formStyles.formInput}
                {...register("name")}
                aria-invalid={errors.name ? "true" : "false"}
                disabled={isEditing && !acl?.find(it => it.name)?.name?.includes("U")}
              />
              <PartFormError name="name" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="email">
                Email Address <sup>*</sup>
              </label>
              <input
                id="email"
                className={formStyles.formInput}
                {...register("email")}
                aria-invalid={errors.email ? "true" : "false"}
                disabled={isEditing && !acl?.find(it => it.email)?.email?.includes("U")}
              />
              <PartFormError name="email" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="role_id">
                Role <sup>*</sup>
              </label>
              <div
                className={formStyles.formSelect}
                data-disabled={
                  isEditing && !acl?.find(it => it.role_id)?.role_id?.includes("U")
                }
              >
                <select
                  id="role_id"
                  data-placeholder={!watch("role_id") ? "" : undefined}
                  {...register("role_id")}
                  aria-invalid={errors.role_id ? "true" : "false"}
                  disabled={
                    isEditing && !acl?.find(it => it.role_id)?.role_id?.includes("U")
                  }
                >
                  <option hidden value="">
                    Please Choose...
                  </option>
                  {roles?.map(it => (
                    <option value={it?.id} key={it?.id}>
                      {it?.name}
                    </option>
                  ))}
                </select>
              </div>
              <PartFormError name="role_id" />
            </div>
          </div>
          <label className={formStyles.formLabel}>
            Program {!isSelectedSuperAdmin && <sup>*</sup>}
          </label>
          <PartFormMultiselect
            maxWidth={592}
            name="program_ids"
            control={control}
            loading={isProgramsLoading}
            options={(() => {
              if (programs) return programs;
              return [];
            })()}
            getOptionValue={option => String(option.id)}
            getOptionLabel={option => option.name}
            disabled={
              (isEditing &&
                !acl?.find(it => it.program_ids)?.program_ids.includes("U")) ||
              // Selected Role === Role Super Admin
              isSelectedSuperAdmin
            }
            placeholder={"Select Program"}
            enableSelectAllOptionMenu
          />
          <PartFormError name="program_ids" />
          <div className={formStyles.formActionButtonsContainer}>
            <button
              type="reset"
              className={formStyles.buttonReset}
              data-test-element-type="button"
              data-test-action-type="reset"
            >
              Discard
            </button>
            <PartFormSaveButton isSubmitting={isSubmitting}>Save</PartFormSaveButton>
          </div>
        </form>
      </PartLoader>
    </FormProvider>
  );
});

UserEditor.displayName = "UserEditor";

const ModalUserEditor = React.forwardRef<PartModalRef>((props, ref) => {
  const { isCreating, isEditing, finishInteraction } = useModalUserEditor();

  const title =
    (isCreating && "Invite New User") || (isEditing && "Edit User") || "Unknown action";

  const [shouldConfirmClose, setShouldConfirmClose] = React.useState<boolean>(false);

  const formRef = React.useRef<IFormInputRef>(null);

  return (
    <PartModal
      ref={ref}
      title={title}
      showed={isCreating || isEditing}
      onClose={() => finishInteraction()}
      afterClose={() => formRef.current?.reset()}
      confirmCloseQuestion={shouldConfirmClose}
    >
      <UserEditor setConfirmClose={setShouldConfirmClose} ref={formRef} />
    </PartModal>
  );
});

ModalUserEditor.displayName = "ModalUserEditor";

export default ModalUserEditor;
